import { config } from '@/config/environment'
import { toast } from 'sonner'

export type EventType = 
  | 'fraud_alert'
  | 'lead_update'
  | 'pattern_match'
  | 'metrics_update'
  | 'team_activity'
  | 'system_alert'
  | 'connected'
  | 'disconnected'
  | 'error'

export interface WebSocketMessage<T = any> {
  type: EventType
  data: T
  timestamp: string
  organization_id: string
  user_id?: string
}

export interface FraudAlertData {
  id: string
  lead_id: string
  risk_score: number
  risk_level: 'low' | 'medium' | 'high' | 'critical'
  patterns_detected: string[]
  recommendation: 'approve' | 'review' | 'block'
  amount?: number
  source?: string
  lead?: {
    email: string
    phone: string
    source: string
  }
  created_at: string
}

export interface MetricsUpdateData {
  leads_today: number
  fraud_detected: number
  amount_saved: number
  detection_rate: number
  active_alerts: number
}

interface ConnectionOptions {
  autoReconnect?: boolean
  reconnectDelay?: number
  maxReconnectDelay?: number
  maxReconnectAttempts?: number
  heartbeatInterval?: number
}

export class WebSocketService {
  private ws: WebSocket | null = null
  private url: string
  private organizationId: string | null = null
  private listeners: Map<EventType, Set<(data: any) => void>> = new Map()
  private reconnectTimer: NodeJS.Timeout | null = null
  private heartbeatTimer: NodeJS.Timeout | null = null
  private simulationTimer: NodeJS.Timeout | null = null
  private reconnectAttempts = 0
  private isIntentionallyClosed = false
  
  private options: Required<ConnectionOptions> = {
    autoReconnect: true,
    reconnectDelay: 1000,
    maxReconnectDelay: 30000,
    maxReconnectAttempts: 10,
    heartbeatInterval: 30000
  }
  
  constructor(options?: ConnectionOptions) {
    this.url = config.wsUrl
    if (options) {
      this.options = { ...this.options, ...options }
    }
  }
  
  async connect(): Promise<void> {
    // Get organization context from auth (mock for now)
    this.organizationId = 'org_' + Math.random().toString(36).substr(2, 9)
    
    // Close existing connection
    if (this.ws) {
      this.disconnect()
    }
    
    this.isIntentionallyClosed = false
    
    return new Promise((resolve, reject) => {
      try {
        // In development/demo mode, simulate WebSocket connection
        if (config.environment === 'development' || config.mockMode) {
          this.simulateConnection(resolve, reject)
          return
        }
        
        // Create real WebSocket connection with auth
        const token = localStorage.getItem('fraud-detection-auth')
        const wsUrl = `${this.url}/ws?org=${this.organizationId}&token=${token}`
        
        this.ws = new WebSocket(wsUrl)
        
        // Connection opened
        this.ws.onopen = () => {
          console.log('WebSocket connected')
          this.reconnectAttempts = 0
          this.startHeartbeat()
          this.emit('connected', null)
          resolve()
        }
        
        // Message received
        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data)
            
            // Filter by organization
            if (message.organization_id !== this.organizationId) {
              return
            }
            
            // Handle different message types
            this.handleMessage(message)
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error)
          }
        }
        
        // Connection closed
        this.ws.onclose = (event) => {
          console.log('WebSocket disconnected:', event.code, event.reason)
          this.stopHeartbeat()
          this.emit('disconnected', { code: event.code, reason: event.reason })
          
          // Auto-reconnect if not intentionally closed
          if (this.options.autoReconnect && !this.isIntentionallyClosed) {
            this.scheduleReconnect()
          }
        }
        
        // Connection error
        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error)
          this.emit('error', error)
          reject(error)
        }
      } catch (error) {
        console.error('Failed to create WebSocket:', error)
        reject(error)
      }
    })
  }
  
  private simulateConnection(resolve: () => void, reject: (error: any) => void): void {
    // Simulate connection delay
    setTimeout(() => {
      console.log('WebSocket connected (simulated)')
      this.reconnectAttempts = 0
      this.emit('connected', null)
      this.startSimulation()
      resolve()
    }, 500)
  }
  
  private startSimulation(): void {
    if (this.simulationTimer) {
      clearInterval(this.simulationTimer)
    }
    
    // Simulate real-time data every 5-15 seconds
    this.simulationTimer = setInterval(() => {
      this.simulateRandomEvent()
    }, Math.random() * 10000 + 5000)
    
    // Initial metrics update
    setTimeout(() => {
      this.simulateMetricsUpdate()
    }, 1000)
  }
  
  private simulateRandomEvent(): void {
    const eventTypes = ['fraud_alert', 'metrics_update', 'lead_update']
    const randomType = eventTypes[Math.floor(Math.random() * eventTypes.length)] as EventType
    
    switch (randomType) {
      case 'fraud_alert':
        this.simulateFraudAlert()
        break
      case 'metrics_update':
        this.simulateMetricsUpdate()
        break
      case 'lead_update':
        this.simulateLeadUpdate()
        break
    }
  }
  
  private simulateFraudAlert(): void {
    const riskLevels: FraudAlertData['risk_level'][] = ['low', 'medium', 'high', 'critical']
    const patterns = [
      ['sequential_phone', 'datacenter_ip'],
      ['velocity_attack'],
      ['suspicious_domain', 'zip_concentration'],
      ['datacenter_ip'],
      ['sequential_phone']
    ]
    
    const alertData: FraudAlertData = {
      id: 'alert_' + Date.now(),
      lead_id: 'lead_' + Math.random().toString(36).substr(2, 8),
      risk_score: Math.floor(Math.random() * 40) + 60, // 60-100
      risk_level: riskLevels[Math.floor(Math.random() * riskLevels.length)],
      patterns_detected: patterns[Math.floor(Math.random() * patterns.length)],
      recommendation: Math.random() > 0.7 ? 'block' : 'review',
      amount: Math.floor(Math.random() * 5000) + 100,
      source: 'everflow',
      lead: {
        email: `user${Math.floor(Math.random() * 1000)}@example.com`,
        phone: `555${Math.floor(Math.random() * 10000000).toString().padStart(7, '0')}`,
        source: Math.random() > 0.5 ? 'everflow' : 'fluent'
      },
      created_at: new Date().toISOString()
    }
    
    const message: WebSocketMessage<FraudAlertData> = {
      type: 'fraud_alert',
      data: alertData,
      timestamp: new Date().toISOString(),
      organization_id: this.organizationId!
    }
    
    this.handleMessage(message)
  }
  
  private simulateMetricsUpdate(): void {
    const metricsData: MetricsUpdateData = {
      leads_today: Math.floor(Math.random() * 500) + 100,
      fraud_detected: Math.floor(Math.random() * 50) + 10,
      amount_saved: Math.floor(Math.random() * 50000) + 5000,
      detection_rate: Math.random() * 20 + 80, // 80-100%
      active_alerts: Math.floor(Math.random() * 10)
    }
    
    const message: WebSocketMessage<MetricsUpdateData> = {
      type: 'metrics_update',
      data: metricsData,
      timestamp: new Date().toISOString(),
      organization_id: this.organizationId!
    }
    
    this.handleMessage(message)
  }
  
  private simulateLeadUpdate(): void {
    const leadData = {
      id: 'lead_' + Math.random().toString(36).substr(2, 8),
      status: Math.random() > 0.5 ? 'approved' : 'rejected',
      risk_score: Math.floor(Math.random() * 100)
    }
    
    const message: WebSocketMessage = {
      type: 'lead_update',
      data: leadData,
      timestamp: new Date().toISOString(),
      organization_id: this.organizationId!
    }
    
    this.handleMessage(message)
  }
  
  disconnect(): void {
    this.isIntentionallyClosed = true
    this.stopHeartbeat()
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    
    if (this.simulationTimer) {
      clearInterval(this.simulationTimer)
      this.simulationTimer = null
    }
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect')
      this.ws = null
    }
  }
  
  private handleMessage(message: WebSocketMessage): void {
    // Emit to specific event listeners
    const listeners = this.listeners.get(message.type)
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(message.data)
        } catch (error) {
          console.error(`Error in WebSocket listener for ${message.type}:`, error)
        }
      })
    }
    
    // Handle special message types
    switch (message.type) {
      case 'fraud_alert':
        this.handleFraudAlert(message.data as FraudAlertData)
        break
      case 'system_alert':
        this.handleSystemAlert(message.data)
        break
    }
  }
  
  private handleFraudAlert(data: FraudAlertData): void {
    // Show toast notification for high-risk alerts
    if (data.risk_level === 'high' || data.risk_level === 'critical') {
      toast.error(
        `${data.risk_level === 'critical' ? '🚨' : '⚠️'} High-risk fraud detected! Score: ${data.risk_score}%`,
        {
          description: `Patterns: ${data.patterns_detected.join(', ')}`,
          duration: data.risk_level === 'critical' ? 10000 : 5000
        }
      )
    }
    
    // Play sound for critical alerts
    if (data.risk_level === 'critical') {
      this.playAlertSound()
    }
  }
  
  private handleSystemAlert(data: any): void {
    if (data.type === 'maintenance') {
      toast.info(`System maintenance scheduled: ${data.message}`, {
        duration: 10000
      })
    }
  }
  
  private playAlertSound(): void {
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime)
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime)
      
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.2)
    } catch (error) {
      console.warn('Could not play alert sound:', error)
    }
  }
  
  // Event subscription
  on<T = any>(event: EventType, callback: (data: T) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    
    this.listeners.get(event)!.add(callback)
    
    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(event)
      if (listeners) {
        listeners.delete(callback)
      }
    }
  }
  
  off(event: EventType, callback?: (data: any) => void): void {
    if (!callback) {
      // Remove all listeners for this event
      this.listeners.delete(event)
    } else {
      // Remove specific listener
      const listeners = this.listeners.get(event)
      if (listeners) {
        listeners.delete(callback)
      }
    }
  }
  
  // Send message to server
  send<T = any>(type: EventType, data: T): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected')
      return
    }
    
    const message: WebSocketMessage<T> = {
      type,
      data,
      timestamp: new Date().toISOString(),
      organization_id: this.organizationId!,
      user_id: localStorage.getItem('user_id') || undefined
    }
    
    this.ws.send(JSON.stringify(message))
  }
  
  // Internal event emitter
  private emit(event: string, data: any): void {
    const listeners = this.listeners.get(event as EventType)
    if (listeners) {
      listeners.forEach(listener => listener(data))
    }
  }
  
  // Heartbeat to keep connection alive
  private startHeartbeat(): void {
    this.stopHeartbeat()
    
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }))
      }
    }, this.options.heartbeatInterval)
  }
  
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }
  
  // Auto-reconnect with exponential backoff
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      toast.error('Connection lost. Please refresh the page.')
      return
    }
    
    const delay = Math.min(
      this.options.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.options.maxReconnectDelay
    )
    
    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`)
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++
      this.connect().catch(console.error)
    }, delay)
  }
  
  // Get connection status
  get isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN || 
           (config.environment === 'development' && this.simulationTimer !== null)
  }
  
  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED
  }
}

// Singleton instance
export const wsService = new WebSocketService()
